import * as React from 'react';
import styles from '../ComponentRulesEditor.module.scss';
import PanelsService from '../../../../services/panels-service';
import { IRulesEntryCondition } from './ComponentRulesDefinitions';
import { FaTrashAlt } from 'react-icons/fa';

interface Props {
    panelService: PanelsService;
    cond: IRulesEntryCondition;
    ruleIndex: number;
    condIndex: number;
    onChange(newCond: IRulesEntryCondition, ruleIndex: number, condIndex: number);
    onRemove(ruleIndex: number, condIndex: number);
}

interface IRuleConditionType {
    type: 'none' | 'bool' | 'number' | 'string' | 'dictionary';
    displayName: string;
}

const ruleValueTypeMap: { [key: string]: IRuleConditionType } = {
    "nothing": { type: "none", displayName: "Nothing" },
    "selectedDictionaryValue": { type: "dictionary", displayName: "Selected Job Config Value" },
    "isPlaceholder": { type: "bool", displayName: "Colour is a Placeholder" },
    "colorName": { type: "string", displayName: "Color Name" },
    "isAffectedByChange": { type: "bool", displayName: "Is Affected By Change" },
    "isFlaggedAtCreateArtwork": { type: "bool", displayName: "Is Flagged at Create Artwork" },
    "isMulticomponent": { type: "bool", displayName: "Swatch is Multicomponent" },
    "componentCount": { type: "number", displayName: "Largest Component Count by Split" },
    "allComponentCount": { type: "number", displayName: "Total Component Count" },
};

export const ComponentRuleCondition: React.FC<Props> = ({ panelService, cond, ruleIndex, condIndex, onChange: doOnChange, onRemove }) => {
    const onChange = (key: string, value: any) => {
        let newCond: IRulesEntryCondition = { ...cond };
        newCond[key] = value;

        if (key === "val" && ruleValueTypeMap[newCond.term].type === "bool") {
            newCond.op = "eq";
        }

        doOnChange(newCond, ruleIndex, condIndex);
    }

    const getValidTagMappings = () => {
        let mappings: PanelDesigner.IMapping[] = [];

        if (panelService.panelTagMappings.length > 0) {
            mappings = panelService.panelTagMappings.filter(m => !m.tag.startsWith('&'));// && !m.isForComponents);
        }

        return mappings;
    }

    let mappings: PanelDesigner.IMapping[] = getValidTagMappings();
    let mappingsDisabled = false;

    if (panelService.panelTagMappings.length == 0) {
        mappings.push({
            name: "-- Loading --",
            displayName: "-- Loading --",
            description: "Please wait...",
            componentsOnly: false,
            tag: "",
            args: []
        });
        mappingsDisabled = true;
    }

    return (
        <>
            <tr>
                <td>
                    <button type="button" className={`btn btn-danger ${styles.removeButton}`} onClick={() => onRemove(ruleIndex, condIndex)}>
                        <FaTrashAlt />
                    </button>
                    {condIndex == 0 ? 'When' : 'And'}
                </td>
                <td colSpan={2}>
                    <select className="form-select" value={cond.term} onChange={e => onChange("term", e.target.value)}>
                        {Object.keys(ruleValueTypeMap).map(ruleType => (
                            <option key={ruleType} value={ruleType}>{ruleValueTypeMap[ruleType].displayName}</option>    
                        ))}
                    </select>
                </td>
                <td>
                    {ruleValueTypeMap[cond.term].type === "bool" && (
                        <div className={styles.inlineCheckboxContainer}>
                            <span> = </span>
                            <input type="checkbox" className="form-check-input" checked={cond.val.trim().toLowerCase() == "true"} onChange={e => onChange("val", e.target.checked.toString())} />
                        </div>
                    )}
                </td>
            </tr>
            <tr hidden={cond.term !== "selectedDictionaryValue"}>
                <td></td>
                <td colSpan={2}>
                    <div className="input-group">
                        <select className="form-select" value={cond.dict} onChange={e => onChange("dict", e.target.value)} disabled={mappingsDisabled}>
                            {mappings.map(mapping => (
                                <option key={mapping.tag} value={mapping.tag} title={mapping.description}>{mapping.displayName}</option>
                            ))}
                        </select>
                    </div>
                </td>
            </tr>
            <tr hidden={ruleValueTypeMap[cond.term].type === "none" || ruleValueTypeMap[cond.term].type === "bool"}>
                <td>
                </td>
                <td>
                    <select className="form-select" value={cond.op} onChange={e => onChange("op", e.target.value)} hidden={ruleValueTypeMap[cond.term].type === "bool" }>
                        <option value="eq">=</option>
                        <option value="neq">!=</option>
                        <option value="lt" hidden={ruleValueTypeMap[cond.term].type === "string"}>&lt;</option>
                        <option value="lte" hidden={ruleValueTypeMap[cond.term].type === "string"}>&lt;=</option>
                        <option value="gt" hidden={ruleValueTypeMap[cond.term].type === "string"}>&gt;</option>
                        <option value="gte" hidden={ruleValueTypeMap[cond.term].type === "string"}>&gt;=</option>
                        <option value="contains" hidden={ruleValueTypeMap[cond.term].type === "number"}>contains</option>
                        <option value="notcontains" hidden={ruleValueTypeMap[cond.term].type === "number"}>does not contain</option>
                        <option value="startswith" hidden={ruleValueTypeMap[cond.term].type === "number"}>starts with</option>
                        <option value="endswith" hidden={ruleValueTypeMap[cond.term].type === "number"}>ends with</option>
                    </select>
                </td>
                <td>
                    {
                        {
                            "none": (
                                <span></span>
                            ),
                            "number": (
                                <input type="number" className="form-control" value={cond.val} onChange={e => onChange("val", e.target.valueAsNumber.toString())} />
                            ),
                            "string": (
                                <input type="text" className="form-control" value={cond.val} onChange={e => onChange("val", e.target.value)} />
                            ),
                            "dictionary": (
                                <input type="text" className="form-control" value={cond.val} onChange={e => onChange("val", e.target.value)} />
                            ),
                        }[ruleValueTypeMap[cond.term].type]
                    }                 
                </td>
            </tr>
        </>
    );
}
